import React, { useEffect, useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { createStructuredSelector } from 'reselect';
import Alert from '@material-ui/lab/Alert';
import { get, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import styled from 'styled-components';
import messages from 'containers/OpeningClaimPageManager/messages';
import { translate } from 'containers/services/translate';
import ClaimDeclaration, {
  claimDeclarationValidator,
} from 'containers/OpeningClaimPageManager/ClaimDeclaration';
import NavigationButtons from 'components/navigationButtons';
import ClaimInformation, {
  claimInformationValidator,
} from 'containers/PreOpeningPage/ClaimInformation';
import OpposingParty from 'containers/PreOpeningPage/OpposingParty/OpposingParty';
import {
  fraudInfoValidator,
  opposingPartyValidator,
} from 'containers/PreOpeningPage/OpposingParty/utils';
import ClaimCircumstances, {
  claimCircumstancesValidator,
} from 'containers/OpeningClaimPageManager/ClaimCircumstances';
import VictimCard from 'containers/PreOpeningPage/VictimCard';
import { victimsValidator } from 'containers/PreOpeningPage/utils';
import {
  requestMakeDecisionClaim,
  requestSearchClaimInfo,
} from 'containers/OpeningClaimPageManager/actions';
import { PAGES } from 'routes';
import routesHelper from 'containers/services/routes-helper';
import {
  makeSelectClaimNumber,
  makeSelectListings,
  makeSelectNotification,
} from 'containers/AppContainer/selectors';
import { Listings } from 'containers/AppContainer/typings';
import { setClaim } from 'containers/PreOpeningPage/actions';
import { makeSelecClaimToPreopen } from 'containers/PreOpeningPage/selectors';
import {
  makeSelectedClaims,
  makeSelectSearchClaims,
} from 'containers/IdentificationPage/selectors';
import Stepper from 'containers/AppContainer/Stepper';
import {
  checkVictimsAge,
  extractOpposingParties,
  checkTotalVictims,
} from 'containers/PreOpeningPage/utils';
import { resolveSagisClaim } from 'helpers/utils';
import {
  isUniqueCinVictims,
  checkClassVictims,
} from 'containers/PreOpeningPage/utils';
import {
  formsAreValidated,
  listOfFormsAreValidated,
} from 'containers/services/validateForm';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import FraudeInformation from 'containers/PreOpeningPage/fraude';
import { makeSelectCorporealLawyers } from 'containers/RecapManagerPage/selectors';
import { DRIVER, CLAIM } from 'containers/data-structure';
import { CHOICE } from 'containers/PreOpeningPage/constants';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { VictimType } from 'typings/globalTypes';

const claimFormValidator = (claimToPreopen: any) => ({
  claimCircumstances: claimCircumstancesValidator(get(claimToPreopen, 'claim')),
  claimInformations: claimInformationValidator(claimToPreopen),
  claimDeclaration: claimDeclarationValidator(get(claimToPreopen, 'driver')),
  victims: victimsValidator(get(claimToPreopen, 'victims')),
  opposingParty: opposingPartyValidator(get(claimToPreopen, 'opposingParty')),
  fraudInfo: fraudInfoValidator(get(claimToPreopen, 'fraudInfo')),
});

export const globalValidator = { claimFormValidator };

export interface OpeningPageManagerProps {
  intl: IntlShape;
  listings: Listings;
  setClaim: Function;
  storedClaimToPreopen: any;
  policyInfo: { sagis: {}; lossDate: Date };
  selectedClaim: any;
  corporealLawyers: any;
  requestSearchClaimInfo: Function;
  claimNumber: string;
  notification: any;
  requestMakeDecisionClaim: Function;
}

const StyledDiv = styled.div`
  height: 24px;
  text-align: left;
  font-family: Roboto;
  letter-spacing: 0;
  color: #384164;
  opacity: 1;
  font-size: 20px;
  margin-top: 50px;
  margin-bottom: 40px;
  font-weight: 100;
`;
export const OpeningPageManager: React.FC<OpeningPageManagerProps> = (
  props,
): JSX.Element => {
  const [claimToPreopen, setClaimToPreopen] = useState({
    claim: {},
    driver: {},
    vehicule: {},
    victims: [],
    documents: get(props.storedClaimToPreopen, 'documents', []),
    opposingParty: {
      hasMaterialDammage: null,
      vehicules: [],
    },
    fraudInfo: get(props.storedClaimToPreopen, 'fraudInfo', []),
  });

  const [claimValidation, setClaimValidation] = React.useState({});

  const handleChange = (claimToPreopen: any) => {
    setClaimToPreopen(claimToPreopen);
  };

  const failedCallBack = () => {
    routesHelper.pushTo(PAGES.MANAGER_HOME)();
  };
  useEffect(() => {
    if (userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER)) {
      if (props.claimNumber) {
        props.requestSearchClaimInfo(props.claimNumber, failedCallBack);
      } else {
        routesHelper.pushTo(PAGES.MANAGER_HOME)();
      }
    }
  }, [props.claimNumber]);
  useEffect(() => {
    setClaimToPreopen({
      claim: get(props.storedClaimToPreopen, 'claim', {}),
      driver: get(props.storedClaimToPreopen, 'driver', {}),
      vehicule: get(props.storedClaimToPreopen, 'vehicule', {}),
      victims: get(props.storedClaimToPreopen, 'victims', []),
      documents: get(props.storedClaimToPreopen, 'documents', []),
      opposingParty: get(props.storedClaimToPreopen, 'opposingParty', {
        hasMaterialDammage: null,
        vehicules: [],
      }),
      fraudInfo: get(props.storedClaimToPreopen, 'fraudInfo', []),
    });
  }, [props.storedClaimToPreopen]);

  const [isOpenAlert, setIsOpenAlert] = React.useState(false);

  const handleNextPageClick = () => {
    props.setClaim(claimToPreopen);
    if (
      !checkTotalVictims(
        // @ts-ignore
        parseInt(claimToPreopen.claim[CLAIM.TOTAL_INJURED_VICTIMS]) || 0,
        // @ts-ignore
        parseInt(claimToPreopen.claim[CLAIM.TOTAL_DECEASED_VICTIMS]) || 0,
        get(claimToPreopen, 'victims', []),
      )
    ) {
      return setIsOpenAlert(true);
    }
    const validator = globalValidator.claimFormValidator(claimToPreopen);
    let expectedValidatorVictims = get(validator, 'victims', []);
    expectedValidatorVictims = expectedValidatorVictims.map(
      (victim: VictimType) => {
        const expectedVictim = { ...victim };
        delete (expectedVictim as any).id;
        return expectedVictim;
      },
    );
    const expectedValidator = {
      claimCircumstances: get(validator, 'claimCircumstances'),
      claimInformations: get(validator, 'claimInformations'),
      claimDeclaration: get(validator, 'claimDeclaration'),
      opposingParty: get(validator, 'opposingParty'),
      fraudInfo:
        get(claimToPreopen, 'claim.suspicious') === CHOICE.YES
          ? get(validator, 'fraudInfo')
          : undefined,
    };

    setClaimValidation(validator);
    if (
      formsAreValidated(expectedValidator) &&
      listOfFormsAreValidated(expectedValidatorVictims) &&
      isUniqueCinVictims(
        get(claimToPreopen, 'victims'),
        get(claimToPreopen, 'claim.lossDate'),
      ) &&
      !checkClassVictims(
        get(claimToPreopen, 'victims'),
        get(claimToPreopen, 'claim.lossDate'),
      ) &&
      !checkVictimsAge(
        get(claimToPreopen, 'victims'),
        get(claimToPreopen, 'claim.lossDate'),
      )
    ) {
      featureService.hasFeature(FEATURES.OPEN_CLAIM_BY_OPENING_MANAGER)
        ? routesHelper.pushTo(PAGES.UPLOAD_DOCUMENTS_MANAGER)()
        : routesHelper.pushTo(PAGES.MANAGER_HOME)();
    }
  };
  return (
    <div>
      <Stepper isOpeningManager />
      <StyledDiv>{translate({ ...messages.completeOpening }, props)}</StyledDiv>
      {isOpenAlert && (
        <Alert
          severity="error"
          style={{ marginBottom: '16px' }}
          onClose={() => {
            setIsOpenAlert(false);
          }}
        >
          {translate(messages.preOpeningPageAlertError, props)}
        </Alert>
      )}
      <ClaimDeclaration
        listings={props.listings}
        updateFilde={handleChange}
        claimToPreopen={claimToPreopen}
        storedClaimToPreopen={props.storedClaimToPreopen}
        policyInfo={props.policyInfo}
        claimDeclarationValidation={get(
          claimValidation,
          'claimDeclaration',
          {},
        )}
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        selectedClaim={0}
      />
      <ClaimInformation
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        policyInfo={props.policyInfo}
        updateFilde={handleChange}
        listings={props.listings}
        claimToPreopen={claimToPreopen}
        storedClaimToPreopen={props.storedClaimToPreopen}
        claimInformationsValidation={get(
          claimValidation,
          'claimInformations',
          {},
        )}
        stakeholders={[]}
      />
      {featureService.hasFeature(FEATURES.OPPOSING_PARTY) && (
        <OpposingParty
          listings={props.listings}
          setClaimToPreopen={setClaimToPreopen}
          claimToPreopen={claimToPreopen}
          opposingPartyValidation={get(claimValidation, 'opposingParty', {})}
          sagisOpposingParties={extractOpposingParties(
            get(resolveSagisClaim(props.policyInfo), 'offeredRisks', []),
          )}
          isConversion
        />
      )}
      <ClaimCircumstances
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        updateFilde={handleChange}
        listings={props.listings}
        claimToPreopen={claimToPreopen}
        storedClaimToPreopen={props.storedClaimToPreopen}
        claimCircumstancesValidation={get(
          claimValidation,
          'claimCircumstances',
          {},
        )}
      />
      <VictimCard
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        claimToPreopen={claimToPreopen}
        listings={props.listings}
        updateFilde={handleChange}
        storedClaimToPreopen={props.storedClaimToPreopen}
        victimsValidation={get(claimValidation, 'victims', [])}
        isConversion
      />
      {featureService.hasFeature(FEATURES.FRAUDE_CORPO) && (
        <FraudeInformation
          isRecapPage={false}
          updateFilde={handleChange}
          claimToPreopen={claimToPreopen}
          storedClaimToPreopen={props.storedClaimToPreopen}
          listings={props.listings}
          fraudInfoValidation={get(claimValidation, 'fraudInfo', {})}
        />
      )}
      <NavigationButtons
        forwardClick={handleNextPageClick}
        onPreviousClick={routesHelper.pushTo(PAGES.MANAGER_HOME)}
        disabledMoveButton={
          isEmpty(get(claimToPreopen, 'victims', [])) ||
          (get(
            get(claimToPreopen, 'driver'),
            [DRIVER.IS_DRIVER_THE_INSURED],
            '',
          ) === CHOICE.NO &&
            (get(get(claimToPreopen, 'driver'), [DRIVER.DATE_OF_BIRTH]) ===
              'Invalid date' ||
              get(get(claimToPreopen, 'driver'), [
                DRIVER.LICENSE_START_DATE,
              ]) === 'Invalid date')) ||
          get(claimToPreopen, 'victims', [])
            .map(
              (el: any) =>
                get(el, 'dateOfBirth') === 'Invalid date' ||
                get(el, 'dateOfBirth') === null,
            )
            .includes(true)
        }
      />
    </div>
  );
};

const mapDispatchToProps = {
  setClaim,
  requestSearchClaimInfo,
  requestMakeDecisionClaim,
};

const mapStateToProps = createStructuredSelector({
  listings: makeSelectListings(),
  storedClaimToPreopen: makeSelecClaimToPreopen(),
  policyInfo: makeSelectSearchClaims(),
  selectedClaim: makeSelectedClaims(),
  corporealLawyers: makeSelectCorporealLawyers(),
  claimNumber: makeSelectClaimNumber(),
  notification: makeSelectNotification(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(OpeningPageManager));
